import { useState } from 'react';
import {
    Search,
    Users,
    Clock,
    MoreVertical,
    UserPlus,
    X,
    CheckCircle,
    UserCheck
} from 'lucide-react';
import { Course } from '../../types';
import { useAdminStore } from '../../stores/adminStore';
import { courseService } from '../../services/courseService';

const DEFAULT_AVATAR = "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y";
const DEFAULT_COURSE_IMAGE = "/course-default-image.png";

enum CurriculumType {
    ORIENTATION = 'ee20f273-17ba-4635-aaf6-d359e38a4aa2',
    ANNUAL = '822991ba-d074-4d68-b32d-ea004ddd9bc8',
    TEST = '10f3863b-b660-41d6-9903-ea4061e4f9a1'
}

const CURRICULUM_LABELS = {
    [CurriculumType.ORIENTATION]: '60 Day Orientation',
    [CurriculumType.ANNUAL]: 'Annual Training Requirements',
    [CurriculumType.TEST]: 'Test'
};

export function AdminCoursesPage() {
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showAssignCourseModal, setShowAssignCourseModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
    const [dueDate, setDueDate] = useState('');
    const [selectedCurriculum, setSelectedCurriculum] = useState<CurriculumType>(CurriculumType.ORIENTATION);
    const [isAssigning, setIsAssigning] = useState(false);
    const { courses, employees, isLoading, error } = useAdminStore();

    const filteredCourses = courses.filter(course => {
        const matchesSearch = 
            (course.title?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
            (course.description?.toLowerCase() || '').includes(searchQuery.toLowerCase());
        const matchesDepartment = selectedDepartment === '' || course.department === selectedDepartment;
        const matchesCurriculum = course.curriculum_id === selectedCurriculum;
        return matchesSearch && matchesDepartment && matchesCurriculum;
    });
    const handleAssignCourse = async () => {
        if (!selectedCourse || selectedEmployees.length === 0) return;
        
        setIsAssigning(true);
        try {
            // Assign course to each selected employee
            const assignments = selectedEmployees.map(employeeId => 
                courseService.assignCourse({
                    userId: employeeId,
                    courseId: selectedCourse.id,
                    dueDate: dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 30 days from now
                })
            );
            
            await Promise.all(assignments);
            
            setShowSuccessModal(true);
            setShowAssignCourseModal(false);
            setSelectedEmployees([]);
            setDueDate('');
            setSelectedCourse(null);
            
            setTimeout(() => setShowSuccessModal(false), 3000);
        } catch (error) {
            console.error('Failed to assign course:', error);
        } finally {
            setIsAssigning(false);
        }
    };

    const AssignModal = () => {
        const filteredEmployees = selectedDepartment 
            ? employees.filter(emp => emp.department === selectedDepartment)
            : employees;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-semibold text-slate-800">
                            View All Employees
                        </h3>
                        <button 
                            onClick={() => {
                                setShowAssignModal(false);
                                setSelectedDepartment('');
                                setSelectedEmployees([]);
                                setDueDate('');
                            }}
                            className="text-slate-600 hover:text-slate-600"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="space-y-4">

                        <div>
                            <label className="block text-sm font-medium text-slate-800 mb-1">
                                Employees
                            </label>
                            <div className="max-h-48 overflow-y-auto border border-slate-300 rounded-lg divide-y divide-slate-200">
                                {filteredEmployees.map(employee => (
                                    <div 
                                        key={employee.id}
                                        className="flex items-center p-3 hover:bg-slate-50"
                                    >
                                        <div className="flex-shrink-0 h-10 w-10">
                                            <img
                                                className="h-10 w-10 rounded-full"
                                                src={employee.image || DEFAULT_AVATAR}
                                                alt={employee.name}
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.src = DEFAULT_AVATAR;
                                                }}
                                            />
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-sm font-medium text-slate-800">{employee.name}</p>
                                            <p className="text-sm text-slate-600">{employee.department}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex justify-center space-x-3 mt-6">
                            <button 
                                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                                onClick={() => setShowAssignModal(false)}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const AssignCourseModal = () => {
        const filteredEmployees = selectedDepartment 
            ? employees.filter(emp => emp.department === selectedDepartment)
            : employees;

        const handleEmployeeToggle = (employeeId: string) => {
            setSelectedEmployees(prev => 
                prev.includes(employeeId) 
                    ? prev.filter(id => id !== employeeId)
                    : [...prev, employeeId]
            );
        };

        const handleSelectAll = () => {
            if (selectedEmployees.length === filteredEmployees.length) {
                setSelectedEmployees([]);
            } else {
                setSelectedEmployees(filteredEmployees.map(emp => emp.id));
            }
        };

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-semibold text-slate-800">
                            Assign Course: {selectedCourse?.title}
                        </h3>
                        <button 
                            onClick={() => {
                                setShowAssignCourseModal(false);
                                setSelectedEmployees([]);
                                setDueDate('');
                                setSelectedCourse(null);
                            }}
                            className="text-slate-600 hover:text-slate-600"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-800 mb-1">
                                Due Date (Optional)
                            </label>
                            <input
                                type="date"
                                value={dueDate}
                                onChange={(e) => setDueDate(e.target.value)}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                min={new Date().toISOString().split('T')[0]}
                            />
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <label className="block text-sm font-medium text-slate-800">
                                    Select Employees ({selectedEmployees.length} selected)
                                </label>
                                <button
                                    onClick={handleSelectAll}
                                    className="text-sm text-primary-600 hover:text-primary-700"
                                >
                                    {selectedEmployees.length === filteredEmployees.length ? 'Deselect All' : 'Select All'}
                                </button>
                            </div>
                            <div className="max-h-64 overflow-y-auto border border-slate-300 rounded-lg divide-y divide-slate-200">
                                {filteredEmployees.map(employee => (
                                    <div 
                                        key={employee.id}
                                        className="flex items-center p-3 hover:bg-slate-50"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedEmployees.includes(employee.id)}
                                            onChange={() => handleEmployeeToggle(employee.id)}
                                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300 rounded mr-3"
                                        />
                                        <div className="flex-shrink-0 h-10 w-10">
                                            <img
                                                className="h-10 w-10 rounded-full"
                                                src={employee.image || DEFAULT_AVATAR}
                                                alt={employee.name}
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.src = DEFAULT_AVATAR;
                                                }}
                                            />
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-sm font-medium text-slate-800">{employee.name}</p>
                                            <p className="text-sm text-slate-600">{employee.department}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        <div className="flex justify-end space-x-3 mt-6">
                            <button 
                                onClick={() => {
                                    setShowAssignCourseModal(false);
                                    setSelectedEmployees([]);
                                    setDueDate('');
                                    setSelectedCourse(null);
                                }}
                                className="px-4 py-2 text-slate-800 bg-slate-100 rounded-lg hover:bg-slate-200"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleAssignCourse}
                                disabled={selectedEmployees.length === 0 || isAssigning}
                                className={`px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center ${
                                    selectedEmployees.length === 0 || isAssigning ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            >
                                <UserCheck className="w-4 h-4 mr-2" />
                                {isAssigning ? 'Assigning...' : `Assign to ${selectedEmployees.length} Employee${selectedEmployees.length !== 1 ? 's' : ''}`}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:space-x-4 sm:space-y-0">
                    <div className="relative">
                        <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-600" />
                        <input
                            type="text"
                            placeholder="Search courses..."
                            className="w-full sm:w-80 pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <select
                        value={selectedCurriculum}
                        onChange={(e) => setSelectedCurriculum(e.target.value as CurriculumType)}
                        className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                    >
                        {Object.entries(CURRICULUM_LABELS).map(([id, label]) => (
                            <option key={id} value={id}>
                                {label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                </div>
            ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-600">{error}</p>
                    <p className="text-sm text-red-500 mt-2">Using mock data as fallback</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {filteredCourses.map(course => (
                        <div key={course.id} className="bg-white rounded-lg shadow-lg overflow-hidden flex flex-col h-full">
                            <div className="flex flex-1">
                                <div className="w-1/3">
                                    <img
                                        src={course.image || DEFAULT_COURSE_IMAGE}
                                        alt={`${course.title} thumbnail`}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.src = DEFAULT_COURSE_IMAGE;
                                        }}
                                    />
                                </div>
                                <div className="w-2/3 p-6">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h3 className="text-lg font-semibold text-slate-800">{course.title}</h3>
                                            <p className="text-sm text-slate-600 mt-1 line-clamp-2">{course.description}</p>
                                        </div>
                                        <button className="text-slate-600 hover:text-slate-600">
                                            <MoreVertical className="w-5 h-5" />
                                        </button>
                                    </div>

                                    <div className="mt-4 space-y-4">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-slate-600">
                                                <Clock className="w-4 h-4 inline mr-1" />
                                                {course.duration}
                                            </span>
                                            <span className="text-slate-600">
                                                <Users className="w-4 h-4 inline mr-1" />
                                                {course.totalEnrolled} Enrolled
                                            </span>
                                        </div>

                                        <div>
                                            <div className="flex justify-between text-sm text-slate-600 mb-1">
                                                <span>Completion Rate</span>
                                                <span>{course.completionRate}%</span>
                                            </div>
                                            <div className="w-full bg-slate-200 rounded-full h-2">
                                                <div
                                                    className="bg-primary-600 h-2 rounded-full"
                                                    style={{ width: `${course.completionRate}%` }}
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <button
                                                onClick={() => {
                                                    setSelectedCourse(course);
                                                    setShowAssignModal(true);
                                                }}
                                                className="w-full flex items-center justify-center px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
                                            >
                                                <UserPlus className="w-4 h-4 mr-2" />
                                                View Employees
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setSelectedCourse(course);
                                                    setShowAssignCourseModal(true);
                                                }}
                                                className="w-full flex items-center justify-center px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                                            >
                                                <UserCheck className="w-4 h-4 mr-2" />
                                                Assign Course
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="border-t border-slate-200 px-6 py-4 min-h-[72px]">
                                <h4 className="text-sm font-medium text-slate-800 mb-3">
                                    Recently Completed By
                                </h4>
                                {course.completedBy && course.completedBy.length > 0 ? (
                                    <div className="flex flex-wrap gap-3">
                                        {course.completedBy.slice(0, 3).map(employee => (
                                            <div
                                                key={employee.id}
                                                className="flex items-center bg-slate-50 rounded-full py-1 pl-1 pr-3"
                                            >
                                                {employee.image || employee.image_url ? (
                                                    <img
                                                        src={employee.image || employee.image_url}
                                                        alt={employee.name}
                                                        className="w-6 h-6 rounded-full mr-2"
                                                    />
                                                ) : (
                                                    <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center mr-2">
                                                        <span className="text-xs font-medium text-primary-600">
                                                            {employee.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                                        </span>
                                                    </div>
                                                )}
                                                <span className="text-sm text-slate-600">{employee.name}</span>
                                            </div>
                                        ))}
                                        {course.completedBy.length > 3 && (
                                            <span className="text-sm text-slate-600 self-center">+{course.completedBy.length - 3} more</span>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-sm text-slate-600 italic">No employees have completed this course yet</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showAssignModal && <AssignModal />}
            {showAssignCourseModal && <AssignCourseModal />}

            {/* Success Notification */}
            {showSuccessModal && (
                <div className="fixed bottom-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Course assigned successfully!</span>
                </div>
            )}
        </div>
    );
} 